import { useCountries } from '@services/common/useCountries'
import { useDegrees } from '@services/common/useDegrees'
import { useJobs } from '@services/common/useJobs'
import { useNationalities } from '@services/common/useNationalities'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'

interface FieldProps {
  value?: string
  onChange?: (value: string | undefined) => void
  error?: string
  required?: boolean
  disabled?: boolean
}

// Most common countries/nationalities pinned to the top of the dropdowns:
// Slovakia, Ukraine, Hungary, Czechia. Names are matched in both locales
// because the API localizes them via Accept-Language.
function buildRanks(groups: string[][]): Record<string, number> {
  const ranks: Record<string, number> = {}
  groups.forEach((names, rank) =>
    names.forEach((name) => {
      ranks[name] = rank
    })
  )
  return ranks
}

const COUNTRY_RANKS = buildRanks([
  ['slovensko', 'slovakia'],
  ['ukrajina', 'ukraine'],
  ['maďarsko', 'hungary'],
  ['česká republika', 'czechia', 'česko']
])

const NATIONALITY_RANKS = buildRanks([
  ['slovenská', 'slovak'],
  ['ukrajinská', 'ukrainian'],
  ['maďarská', 'hungarian'],
  ['česká', 'czech']
])

/** Stable sort: pinned entries first (in pin order), the rest keep API order. */
function prioritize(
  options: ComboboxOption[],
  ranks: Record<string, number>
): ComboboxOption[] {
  return [...options].sort(
    (a, b) =>
      (ranks[a.label.toLowerCase()] ?? Infinity) -
      (ranks[b.label.toLowerCase()] ?? Infinity)
  )
}

function FieldShell({
  label,
  required,
  error,
  children
}: {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <Label className="mb-1.5 block">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error && (
        <p className="mt-1.5 text-sm font-medium text-destructive">{error}</p>
      )}
    </div>
  )
}

export function CountryField({
  value,
  onChange,
  error,
  required,
  disabled
}: FieldProps) {
  const { t } = useTranslation('users')
  const [search, setSearch] = useState('')
  const { data, isLoading } = useCountries({ search })

  const options: ComboboxOption[] = prioritize(
    (data ?? []).map((country) => ({
      label: country.name,
      value: String(country.id)
    })),
    COUNTRY_RANKS
  )

  return (
    <FieldShell
      label={t('form.labels.country')}
      required={required}
      error={error}
    >
      <Combobox
        value={value}
        onChange={onChange}
        onSearch={setSearch}
        options={options}
        loading={isLoading}
        invalid={!!error}
        disabled={disabled}
        placeholder={t('form.placeholders.countrySelectPlaceholder')}
      />
    </FieldShell>
  )
}

export function NationalityField({
  value,
  onChange,
  error,
  required,
  disabled
}: FieldProps) {
  const { t } = useTranslation('users')
  const [searchParams, setSearchParams] = useState('')
  const { data, isLoading } = useNationalities({ searchParams })

  const options: ComboboxOption[] = prioritize(
    (data ?? []).map((nationality) => ({
      label: nationality.name,
      value: String(nationality.id)
    })),
    NATIONALITY_RANKS
  )

  return (
    <FieldShell
      label={t('form.labels.nationality')}
      required={required}
      error={error}
    >
      <Combobox
        value={value}
        onChange={onChange}
        onSearch={setSearchParams}
        options={options}
        loading={isLoading}
        invalid={!!error}
        disabled={disabled}
        placeholder={t('form.placeholders.nationality')}
      />
    </FieldShell>
  )
}

export function DegreeField({
  value,
  onChange,
  error,
  required,
  disabled
}: FieldProps) {
  const { t } = useTranslation('users')
  const [search, setSearch] = useState('')
  const { data, isLoading } = useDegrees({ search })

  const options: ComboboxOption[] = (data ?? []).map((degree) => ({
    label: degree.name,
    value: String(degree.id)
  }))

  return (
    <FieldShell
      label={t('form.labels.degree')}
      required={required}
      error={error}
    >
      <Combobox
        value={value}
        onChange={onChange}
        onSearch={setSearch}
        options={options}
        loading={isLoading}
        invalid={!!error}
        disabled={disabled}
        clearable
        placeholder={t('form.placeholders.degree')}
      />
    </FieldShell>
  )
}

export function JobField({
  value,
  onChange,
  error,
  required,
  disabled
}: FieldProps) {
  const { t } = useTranslation('users')
  const [searchParams, setSearchParams] = useState('')
  const { data, isLoading } = useJobs({ searchParams })

  const options: ComboboxOption[] = (data ?? []).map((job) => ({
    label: job.name,
    value: String(job.id)
  }))

  return (
    <FieldShell label={t('form.labels.job')} required={required} error={error}>
      <Combobox
        value={value}
        onChange={onChange}
        onSearch={setSearchParams}
        options={options}
        loading={isLoading}
        invalid={!!error}
        disabled={disabled}
        placeholder={t('form.placeholders.job')}
      />
    </FieldShell>
  )
}
